import { FormattedMessage } from 'react-intl'

import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import Divider from 'LuxKit/Divider'
import BodyText from 'LuxKit/Typography/BodyText'
import CruiseSoldoutDepartureCard from 'components/OfferPage/Cruises/CruiseSoldoutDepartureCard'
import CruiseSoldoutStillInterested from 'components/OfferPage/Cruises/CruiseSoldoutStillInterested'
import Sheet from 'components/utils/Sheet'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'

import CruiseDepartureFilters from './CruiseDepartureFilters'
import NeedHelp from '../NeedHelp'
import CruiseDepartureTile from './CruiseDepartureTile/CruiseDepartureTile'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import OfferPageLoyaltyPointsSlider from 'components/OfferPage/Loyalty/OfferPageLoyaltyPointsSlider'
import useCruiseDeparturesModel from 'hooks/Cruise/useCruiseDeparturesModel'
import type { DeparturesByDate } from 'lib/cruises/cruiseDepartures'

export type { DeparturesByDate } from 'lib/cruises/cruiseDepartures'

interface Props {
  offer: App.CruiseOffer
  departuresByDate: DeparturesByDate
  hasValidDepartures: boolean
  onHandleSelectDeparture: (departure: App.CruiseDepartureDetails) => void
  offerPageOccupancies: Array<App.Occupants>
  setOfferPageOccupancies: (occupancies: Array<App.Occupants>) => void
  onOfferPageOccupancyApplied?: () => void
  isCruiseOfferTravellerPickerVariant: boolean
  isWeddingGiftRegistry?: boolean
}

function CruiseOfferPageDepartures({
  offer,
  departuresByDate,
  hasValidDepartures,
  onHandleSelectDeparture,
  offerPageOccupancies,
  setOfferPageOccupancies,
  onOfferPageOccupancyApplied,
  isCruiseOfferTravellerPickerVariant,
  isWeddingGiftRegistry = false,
}: Props) {
  const {
    departureCountByYear,
    departuresByMonth,
    hasMultipleDepartures,
    referenceDepartureDateForOccupancy,
    selectedYear,
    setSelectedYear,
    setSort,
    sort,
    totalDepartureCount,
    yearOptions,
  } = useCruiseDeparturesModel({
    departuresByDate,
    lowestDeparturePricesByCategory: offer.lowestDeparturePricesByCategory,
  })

  return <LayoutContainer>
    <ScrollAnchor id="departures-anchor" />

    <Sheet padding="32 0">
      <Group direction="vertical" gap={40}>
        <Group direction="vertical" gap={24}>
          <Heading variant="heading3" align="center" className="section-title">
            <FormattedMessage
              id="A51U7aAfppFcGWSpIvSj"
              defaultMessage="Dates & Pricing"
              description="Section heading for cruise departure dates and pricing"
            />
          </Heading>

          <Divider kind="secondary" />
        </Group>

        <OfferPageLoyaltyPointsSlider />

        <CruiseDepartureFilters
          sort={sort}
          yearOptions={yearOptions}
          selectedYear={selectedYear}
          totalDepartureCount={totalDepartureCount}
          departureCountByYear={departureCountByYear}
          setSort={setSort}
          setSelectedYear={setSelectedYear}
          ship={offer.ship}
          offerPageOccupancies={offerPageOccupancies}
          setOfferPageOccupancies={setOfferPageOccupancies}
          onOfferPageOccupancyApplied={onOfferPageOccupancyApplied}
          referenceDepartureDateForOccupancy={referenceDepartureDateForOccupancy ?? ''}
          isCruiseOfferTravellerPickerVariant={isCruiseOfferTravellerPickerVariant}
          hasMultipleDepartures={hasMultipleDepartures}
        />

        <Group direction="vertical" gap={36}>
          {departuresByMonth.map(([dateLabel, departures]) => (
            <VerticalSpacer gap={20} key={dateLabel}>
              <VerticalSpacer gap={16}>
                <BodyText variant="large" weight="bold" align="start">{dateLabel}</BodyText>
                <CSSBreakpoint only="mobile">
                  <Divider kind="secondary" direction="horizontal" />
                </CSSBreakpoint>
              </VerticalSpacer>

              {departures.map(({ departure }) => (
                <CruiseDepartureTile
                  offer={offer}
                  key={departure.id}
                  departure={departure}
                  onHandleDepartureModal={onHandleSelectDeparture}
                  isWeddingGiftRegistry={isWeddingGiftRegistry}
                />
              ))}
            </VerticalSpacer>
          ))}

          {!hasValidDepartures && <CruiseSoldoutDepartureCard />}
          {!hasValidDepartures && <CruiseSoldoutStillInterested useLinkButton />}
          {hasValidDepartures && <NeedHelp />}
        </Group>
      </Group>
    </Sheet>
  </LayoutContainer>
}

export default CruiseOfferPageDepartures
